import React, { useState, useEffect, useRef } from 'react'
import { FiFilter, FiX, FiRefreshCcw } from 'react-icons/fi';
import { FaDownload, FaSearch, FaSortUp, FaSortDown } from "react-icons/fa";
import { BsFillInfoCircleFill, BsCheckCircle, BsXCircle, BsClock } from "react-icons/bs";
import { sendGlobalWS, useWebSocket } from '../../context/UseWebSocketContext';
import { toast } from 'react-toastify';
import { Modal, Button } from 'react-bootstrap';

const OrderTable = ({ darkMode = false }) => {
    const [sortOrder, setSortOrder] = useState("desc");
    const [searchText, setSearchText] = useState(''); 
    const [sortColumn, setSortColumn] = useState('created');
    const [currentPage, setCurrentPage] = useState(1);
    const [isSearchExpanded, setIsSearchExpanded] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const searchContainerRef = useRef(null);
    const searchInputRef = useRef(null);
    const rowsPerPage = 15;
    const isMountedRef = useRef(true);

    // ✅ Get orders directly from WebSocket
    const { ordersData } = useWebSocket();
    const orders = Array.isArray(ordersData) ? ordersData : [];

    // ✅ Cleanup on unmount
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    // ✅ Stop loading when data arrives
    useEffect(() => {
        if (isLoading && isMountedRef.current) {
            setIsLoading(false);
            console.log("✅ Orders loaded, count:", orders.length);
        }
    }, [orders, isLoading]);

    // ✅ Load orders on mount
    useEffect(() => {
        console.log("🔄 Loading orders...");
        sendGlobalWS({ r: "orders" });
        
        const timeout = setTimeout(() => {
            if (isMountedRef.current && isLoading) {
                setIsLoading(false);
            }
        }, 8000);
        
        return () => clearTimeout(timeout);
    }, []);

    // Apply dark mode
    useEffect(() => {
        if (darkMode) {
            document.body.classList.add('dark-mode');
        } else {
            document.body.classList.remove('dark-mode');
        }
    }, [darkMode]);

    useEffect(() => {
        setCurrentPage(1);
    }, [orders, searchText]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchContainerRef.current && !searchContainerRef.current.contains(event.target) && !searchText) {
                setIsSearchExpanded(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [searchText]);

    const handleRefresh = () => {
        console.log("🔄 Manual refresh triggered");
        setIsLoading(true);
        sendGlobalWS({ r: "orders" });
        toast.info("Refreshing orders...");
        
        setTimeout(() => {
            if (isMountedRef.current && isLoading) {
                setIsLoading(false);
            }
        }, 8000);
    };

    const handleDownload = () => {
        if (!orders || orders.length === 0) {
            toast.warning("No data to download!");
            return;
        }

        const headers = ['Created', 'Order Id', 'Account', 'Symbol', 'Order Type', 'Quantity', 'Fill Price', 'Status', 'Error Message'];
        const rows = orders.map(row => [
            row.created,
            row.order_id,
            row.account,
            row.trading_symbol,
            row.order_type,
            row.quantity,
            row.fill_price,
            row.status,
            row.error_msg || '-'
        ]);

        const csvContent = [headers, ...rows].map(row => 
            row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
        ).join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
        a.href = url;
        a.download = `orders-data-${timestamp}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Download started!");
    };

    const handleSort = (column) => {
        if (sortColumn === column) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            setSortColumn(column);
            setSortOrder("asc");
        }
    };

    const getSortIcon = (column) => {
        if (sortColumn !== column) return <FiFilter size={9} className="opacity-50" />;
        return sortOrder === "asc" ? <FaSortUp size={9} /> : <FaSortDown size={9} />;
    };

    const handleSearchExpand = () => {
        setIsSearchExpanded(true);
        setTimeout(() => {
            searchInputRef.current?.focus();
        }, 100);
    };

    const clearSearch = () => {
        setSearchText('');
        setIsSearchExpanded(false);
        setCurrentPage(1);
    };

    const handleShowDetails = (order) => {
        setSelectedOrder(order);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedOrder(null);
    };

    // Filter and sort data
    let filteredData = [...orders].filter(row => {
        const searchLower = searchText.toLowerCase();
        if (!searchLower) return true;
        return (
            row.trading_symbol?.toLowerCase().includes(searchLower) ||
            row.order_id?.toLowerCase().includes(searchLower) ||
            row.account?.toLowerCase().includes(searchLower) ||
            row.status?.toLowerCase().includes(searchLower)
        );
    });

    filteredData = filteredData.filter((row, index, self) => 
        index === self.findIndex(r => r.order_id === row.order_id)
    );

    filteredData.sort((a, b) => {
        let aVal = a[sortColumn] || '';
        let bVal = b[sortColumn] || '';
        
        if (sortColumn === 'created') {
            aVal = new Date(aVal);
            bVal = new Date(bVal);
        } else {
            aVal = String(aVal).toLowerCase();
            bVal = String(bVal).toLowerCase();
        }
        
        if (sortOrder === "asc") {
            return aVal > bVal ? 1 : -1;
        } else {
            return aVal < bVal ? 1 : -1;
        }
    });

    const totalPages = Math.ceil(filteredData.length / rowsPerPage);
    const startIdx = (currentPage - 1) * rowsPerPage;
    const currentRows = filteredData.slice(startIdx, startIdx + rowsPerPage);

    const getStatusIcon = (status) => {
        const statusLower = status?.toLowerCase() || '';
        if (statusLower.includes('filled')) return <BsCheckCircle className="text-green-500" size={9} />;
        if (statusLower.includes('pending')) return <BsClock className="text-yellow-500" size={9} />;
        if (statusLower.includes('cancel')) return <BsXCircle className="text-red-500" size={9} />;
        return null;
    };

    const getStatusColor = (status) => {
        const statusLower = status?.toLowerCase() || '';
        if (statusLower.includes('filled')) return 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/40';
        if (statusLower.includes('pending')) return 'text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/40';
        if (statusLower.includes('cancel')) return 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/40';
        return 'text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800';
    };

    // CSS classes
    const containerClass = `transition-colors duration-200 h-full flex flex-col bg-[var(--bg-primary)]`;
    const cardClass = `rounded-lg overflow-hidden transition-colors duration-200 flex flex-col h-full
        bg-[var(--bg-card)] border border-[var(--border-color)] shadow-md`;
    const buttonClass = `p-1 rounded-md transition-all duration-200 text-[var(--text-muted)] hover:bg-[var(--bg-hover)]`;
    const tableHeaderClass = `sticky top-0 z-10 transition-colors duration-200 text-[11px] font-semibold bg-[var(--bg-header)] text-[var(--text-primary)]`;
    const tableCellClass = `px-2 py-1 border text-[11px] transition-colors duration-200 border-[var(--border-color)] text-[var(--text-secondary)]`;
    const searchBoxClass = `relative flex items-center rounded-md transition-all duration-300
        bg-[var(--input-bg)] border border-[var(--input-border)]
        ${isSearchExpanded ? 'w-52' : 'w-auto'}`;
    const searchInputClass = `w-full outline-none text-[11px] py-1 px-1 bg-transparent text-[var(--input-text)] placeholder:text-[var(--text-muted)]`;

    return (
        <div className={containerClass}>
            <div className={cardClass}>
                {/* Header */}
                <div className="flex-shrink-0 px-2 py-1 border-b border-[var(--border-color)]">
                    <div className="flex items-center justify-between gap-2">
                        {/* Search */}
                        <div className="flex items-center gap-1">
                            <div ref={searchContainerRef} className={searchBoxClass}>
                                <button
                                    onClick={handleSearchExpand}
                                    className={`p-1 rounded-l-md transition-all duration-200
                                        ${isSearchExpanded 
                                            ? 'bg-blue-500 text-white'
                                            : 'hover:bg-[var(--bg-hover)] text-[var(--text-muted)]'
                                        }`}
                                >
                                    <FaSearch size={10} />
                                </button>
                                
                                <div className={`overflow-hidden transition-all duration-300 ${isSearchExpanded ? 'w-36' : 'w-0'}`}>
                                    <input
                                        ref={searchInputRef}
                                        type="text"
                                        placeholder="Search..."
                                        className={searchInputClass}
                                        value={searchText}
                                        onChange={(e) => {
                                            setSearchText(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                    />
                                </div>
                                
                                {isSearchExpanded && searchText && (
                                    <button
                                        onClick={clearSearch}
                                        className={`p-1 rounded-r-md transition-all duration-200 hover:bg-[var(--bg-hover)] text-[var(--text-muted)]`}
                                    >
                                        <FiX size={10} />
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-1">
                            <button onClick={handleDownload} className={buttonClass} title="Download">
                                <FaDownload size={11} />
                            </button>
                            <button onClick={handleRefresh} className={buttonClass} title="Refresh">
                                <FiRefreshCcw size={11} />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Loading Indicator */}
                {isLoading && (
                    <div className="flex items-center justify-center py-3 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-200 dark:border-blue-800">
                        <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                        <span className="ml-2 text-[11px] text-blue-600 dark:text-blue-400 font-medium">
                            Loading orders...
                        </span>
                    </div>
                )}

                {/* Table */}
                <div className="flex-1 overflow-auto min-h-0">
                    <table className="w-full h-full">
                        <thead className={tableHeaderClass}>
                            <tr>
                                <th className="px-2 py-1 border-b text-left font-semibold w-28 sticky top-0 bg-inherit border-[var(--border-color)]">
                                    <div className="flex items-center gap-0.5 text-gray-400 cursor-pointer" onClick={() => handleSort('created')}>
                                        Created
                                        <span className="text-blue-500">{getSortIcon('created')}</span>
                                    </div>
                                </th>
                                <th className="px-2 py-1 border-b text-left font-semibold w-28 sticky top-0 bg-inherit border-[var(--border-color)]">
                                    <div className="flex items-center gap-0.5 text-gray-400 cursor-pointer" onClick={() => handleSort('order_id')}>
                                        Order Id
                                        <span className="text-blue-500">{getSortIcon('order_id')}</span>
                                    </div>
                                </th>
                                <th className="px-2 py-1 border-b text-left font-semibold w-20 sticky top-0 bg-inherit border-[var(--border-color)]">
                                    <div className="flex items-center gap-0.5 text-gray-400 cursor-pointer" onClick={() => handleSort('account')}>
                                        Account
                                        <span className="text-blue-500">{getSortIcon('account')}</span>
                                    </div>
                                </th>
                                <th className="px-2 py-1 border-b text-left font-semibold w-24 sticky top-0 bg-inherit border-[var(--border-color)]">
                                    <div className="flex items-center gap-0.5 text-gray-400 cursor-pointer" onClick={() => handleSort('trading_symbol')}>
                                        Symbol
                                        <span className="text-blue-500">{getSortIcon('trading_symbol')}</span>
                                    </div>
                                </th>
                                <th className="px-2 py-1 border-b text-left font-semibold w-20 sticky top-0 bg-inherit border-[var(--border-color)]">
                                    <div className="flex items-center gap-0.5 text-gray-400 cursor-pointer" onClick={() => handleSort('order_type')}>
                                        Order Type
                                        <span className="text-blue-500">{getSortIcon('order_type')}</span>
                                    </div>
                                </th>
                                <th className="px-2 py-1 border-b text-left font-semibold w-16 sticky top-0 bg-inherit border-[var(--border-color)]">
                                    <div className="flex items-center gap-0.5 text-gray-400 cursor-pointer" onClick={() => handleSort('quantity')}>
                                        Quantity
                                        <span className="text-blue-500">{getSortIcon('quantity')}</span>
                                    </div>
                                </th>
                                <th className="px-2 py-1 border-b text-left font-semibold w-20 sticky top-0 bg-inherit border-[var(--border-color)]">
                                    <div className="flex items-center gap-0.5 text-gray-400 cursor-pointer" onClick={() => handleSort('fill_price')}>
                                        Fill Price
                                        <span className="text-blue-500">{getSortIcon('fill_price')}</span>
                                    </div>
                                </th>
                                <th className="px-2 py-1 border-b text-left font-semibold w-24 sticky top-0 bg-inherit border-[var(--border-color)]">
                                    <div className="flex items-center gap-0.5 text-gray-400 cursor-pointer" onClick={() => handleSort('status')}>
                                        Status
                                        <span className="text-blue-500">{getSortIcon('status')}</span>
                                    </div>
                                </th>
                                <th className="px-2 py-1 border-b text-left text-gray-400 font-semibold w-28 sticky top-0 bg-inherit border-[var(--border-color)]">
                                    Error Message
                                </th>
                                <th className="px-2 py-1 border-b text-left text-gray-400 font-semibold w-10 sticky top-0 bg-inherit border-[var(--border-color)]">
                                    Info
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                Array.from({ length: 5 }).map((_, idx) => (
                                    <tr key={`skeleton-${idx}`} className="animate-pulse">
                                        <td className={tableCellClass}><div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-20"></div></td>
                                        <td className={tableCellClass}><div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-16"></div></td>
                                        <td className={tableCellClass}><div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-12"></div></td>
                                        <td className={tableCellClass}><div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-20"></div></td>
                                        <td className={tableCellClass}><div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-14"></div></td>
                                        <td className={tableCellClass}><div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-10"></div></td>
                                        <td className={tableCellClass}><div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-14"></div></td>
                                        <td className={tableCellClass}><div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-12"></div></td>
                                        <td className={tableCellClass}><div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-24"></div></td>
                                        <td className={tableCellClass}><div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-6"></div></td>
                                    </tr>
                                ))
                            ) : currentRows.length === 0 ? (
                                <tr className="h-full">
                                    <td colSpan={10} className="h-full">
                                        <div className="flex flex-col items-center justify-center h-full min-h-[300px]">
                                            <div className="text-3xl text-[var(--text-muted)]">📋</div>
                                            <p className="text-xs text-gray-400 font-medium mt-2 text-[var(--text-secondary)]">
                                                No orders found
                                            </p>
                                            <p className="text-[10px] mt-1 text-[var(--text-light)]">
                                                {searchText ? 'Try adjusting your search' : 'No orders available'}
                                            </p>
                                            <button 
                                                onClick={handleRefresh}
                                                className="mt-3 px-3 py-1 text-[10px] rounded-md bg-blue-500 text-white hover:bg-blue-600"
                                            >
                                                Refresh
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                currentRows.map((row, idx) => (
                                    <tr key={row.order_id || idx} className="transition-colors duration-150 hover:bg-[var(--bg-hover)]">
                                        <td className={tableCellClass}>
                                            <div className="text-[10px] font-mono">
                                                {new Date(row.created).toLocaleString()}
                                            </div>
                                        </td>
                                        <td className={`${tableCellClass} font-mono text-[10px]`}>
                                            {row.order_id?.substring(0, 12)}..
                                        </td>
                                        <td className={tableCellClass}>
                                            {row.account}
                                        </td>
                                        <td className={`${tableCellClass} font-medium`}>
                                            {row.trading_symbol}
                                        </td>
                                        <td className={tableCellClass}>
                                            <span className={`px-1 py-0.5 rounded text-[9px] font-medium bg-[var(--bg-hover)]`}>
                                                {row.order_type === 'MARKET' ? 'MKT' : row.order_type === 'LIMIT' ? 'LMT' : row.order_type}
                                            </span>
                                        </td>
                                        <td className={tableCellClass}>
                                            {row.quantity}
                                        </td>
                                        <td className={tableCellClass}>
                                            ₹{row.fill_price?.toFixed(2) || '0.00'}
                                        </td>
                                        <td className={tableCellClass}>
                                            <div className="flex items-center gap-0.5">
                                                {getStatusIcon(row.status)}
                                                <span className={`px-1 py-0.5 rounded text-[9px] font-medium ${getStatusColor(row.status)}`}>
                                                    {row.status === 'Filled' ? 'OK' : row.status === 'Pending' ? 'PEND' : row.status === 'Cancelled' ? 'CAN' : row.status?.substring(0, 3)}
                                                </span>
                                            </div>
                                        </td>
                                        <td className={tableCellClass}>
                                            <div className="max-w-28 truncate text-[10px]" title={row.error_msg}>
                                                {row.error_msg || '-'}
                                            </div>
                                        </td>
                                        <td className={tableCellClass}>
                                            <button 
                                                onClick={() => handleShowDetails(row)} 
                                                className="p-0.5 rounded transition-all duration-200 hover:bg-[var(--bg-hover)] text-[var(--text-muted)]"
                                                title="View Details"
                                            >
                                                <BsFillInfoCircleFill size={10} />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {!isLoading && totalPages > 1 && (
                    <div className="flex-shrink-0 flex justify-between items-center px-2 py-1 border-t text-[10px] border-[var(--border-color)]">
                        <div className="text-[10px] text-[var(--text-muted)]">
                            {startIdx + 1}-{Math.min(startIdx + rowsPerPage, filteredData.length)} of {filteredData.length}
                        </div>
                        <div className="flex gap-0.5">
                            <button
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                disabled={currentPage === 1}
                                className={`px-1 py-0.5 text-[10px] rounded transition-all
                                    ${currentPage === 1 
                                        ? 'text-[var(--text-light)] cursor-not-allowed'
                                        : 'hover:bg-[var(--bg-hover)] text-[var(--text-muted)]'
                                    }`}
                            >
                                ←
                            </button>
                            
                            {Array.from({ length: Math.min(3, totalPages) }, (_, i) => {
                                let pageNum;
                                if (totalPages <= 3) {
                                    pageNum = i + 1;
                                } else if (currentPage <= 2) {
                                    pageNum = i + 1;
                                } else if (currentPage >= totalPages - 1) {
                                    pageNum = totalPages - 2 + i;
                                } else {
                                    pageNum = currentPage - 1 + i;
                                }
                                
                                return (
                                    <button
                                        key={pageNum}
                                        onClick={() => setCurrentPage(pageNum)}
                                        className={`w-5 h-4 text-[10px] rounded transition-all
                                            ${currentPage === pageNum
                                                ? 'bg-blue-500 text-white'
                                                : 'hover:bg-[var(--bg-hover)] text-[var(--text-muted)]'
                                            }`}
                                    >
                                        {pageNum}
                                    </button>
                                );
                            })}
                            
                            <button
                                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                disabled={currentPage === totalPages}
                                className={`px-1 py-0.5 text-[10px] rounded transition-all
                                    ${currentPage === totalPages 
                                        ? 'text-[var(--text-light)] cursor-not-allowed'
                                        : 'hover:bg-[var(--bg-hover)] text-[var(--text-muted)]'
                                    }`}
                            >
                                →
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Modal */}
            <Modal show={showModal} onHide={handleCloseModal} centered size="sm">
                <Modal.Header closeButton className={`py-1.5 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-100'}`}>
                    <Modal.Title className={`text-xs ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        Order Details
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className={`p-2 ${darkMode ? 'bg-gray-900 text-gray-300' : 'bg-white text-gray-700'}`}>
                    {selectedOrder && (
                        <div className="space-y-1.5 text-[11px]">
                            <div className="grid grid-cols-2 gap-1.5">
                                <div>
                                    <label className="text-[9px] font-semibold text-gray-500">Order Id</label>
                                    <p className="text-[10px] font-mono">{selectedOrder.order_id}</p>
                                </div>
                                <div>
                                    <label className="text-[9px] font-semibold text-gray-500">Created</label>
                                    <p className="text-[10px]">{new Date(selectedOrder.created).toLocaleString()}</p>
                                </div>
                                <div>
                                    <label className="text-[9px] font-semibold text-gray-500">Account</label>
                                    <p className="text-[10px]">{selectedOrder.account}</p>
                                </div>
                                <div>
                                    <label className="text-[9px] font-semibold text-gray-500">Symbol</label>
                                    <p className="text-[10px] font-semibold">{selectedOrder.trading_symbol}</p>
                                </div>
                                <div>
                                    <label className="text-[9px] font-semibold text-gray-500">Order Type</label>
                                    <p className="text-[10px]">{selectedOrder.order_type}</p>
                                </div>
                                <div>
                                    <label className="text-[9px] font-semibold text-gray-500">Quantity</label>
                                    <p className="text-[10px]">{selectedOrder.quantity}</p>
                                </div>
                                <div>
                                    <label className="text-[9px] font-semibold text-gray-500">Fill Price</label>
                                    <p className="text-[10px]">₹{selectedOrder.fill_price?.toFixed(2)}</p>
                                </div>
                                <div>
                                    <label className="text-[9px] font-semibold text-gray-500">Status</label>
                                    <p className={`text-[10px] font-semibold ${
                                        selectedOrder.status === 'Filled' ? 'text-green-600' :
                                        selectedOrder.status === 'Pending' ? 'text-yellow-600' :
                                        selectedOrder.status === 'Cancelled' ? 'text-red-600' : ''
                                    }`}>
                                        {selectedOrder.status}
                                    </p>
                                </div>
                                <div className="col-span-2">
                                    <label className="text-[9px] font-semibold text-gray-500">Error Message</label>
                                    <p className="text-[10px]">{selectedOrder.error_msg || 'No error'}</p>
                                </div>
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className={`py-1.5 ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                    <Button size="sm" variant="secondary" onClick={handleCloseModal} className="text-[10px] py-1">
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default OrderTable;